import {
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
  type ColumnFiltersState,
  type OnChangeFn,
  type SortingState,
  type Table,
  type Updater,
} from '@tanstack/react-table';
import { useState } from 'react';
import { defaultColumnFilterFn } from '@avanta/shared/utils/table';

type PaginationState = { pageIndex: number; pageSize: number };

export interface UseServerPaginatedTableProps<T> {
  columns: ColumnDef<T>[];
  data: T[];
  totalCount: number;
  pagination: PaginationState;
  onPaginationChange: (updater: Updater<PaginationState>) => void;
  globalFilter: string;
  onGlobalFilterChange: (updater: Updater<string>) => void;
  columnFilters?: ColumnFiltersState;
  onColumnFiltersChange?: OnChangeFn<ColumnFiltersState>;
}

export const useServerPaginatedTable = <T>({
  columns,
  data,
  totalCount,
  pagination,
  onPaginationChange,
  globalFilter,
  onGlobalFilterChange,
  columnFilters,
  onColumnFiltersChange,
}: UseServerPaginatedTableProps<T>): Table<T> => {
  const pageCount = totalCount > 0 ? Math.ceil(totalCount / pagination.pageSize) : 0;
  const [sorting, setSorting] = useState<SortingState>([]);

  return useReactTable({
    columns,
    data,
    defaultColumn: {
      enableSorting: false,
      filterFn: defaultColumnFilterFn,
    },
    manualPagination: true,
    manualFiltering: true,
    pageCount,
    state: {
      pagination,
      globalFilter,
      columnFilters,
      sorting,
    },
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onGlobalFilterChange: onGlobalFilterChange,
    onColumnFiltersChange,
    onSortingChange: setSorting,
    onPaginationChange: onPaginationChange,
  });
};
